import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES, PROJECTS } from "@/lib/site";

const FEATURES = [
  "Complete bathroom renovations from design to final detail",
  "Custom vanities built for your space and storage needs",
  "Quality tile, stone, fixtures, and finishes",
  "Modern, functional layouts that make the most of your space",
  "Careful waterproofing and professional installation",
  "Serving bathrooms of every size across the GTA",
];

const bathProjects = PROJECTS.filter((p) => ["Bathrooms", "Vanities"].includes(p.cat));

export default function Bathrooms() {
  return (
    <main data-testid="bathrooms-page">
      <SEO
        title="Bathroom Renovations & Custom Vanities GTA | Hive Interiors"
        description="Modern bathroom renovations and custom vanities across the GTA. Quality materials, thoughtful design and professional installation from Hive Interiors."
      />
      <PageHero
        image={IMAGES.bathHero}
        overline="Bathrooms"
        title="Bathroom Renovations"
        italic="& Custom Vanities"
        sub="Modern, functional bathrooms designed with quality materials and attention to detail — from a custom vanity to a complete renovation."
        alt="Renovated luxury bathroom with marble finishes"
      />

      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <Overline>01 / Your Bathroom, Reimagined</Overline>
              <h2 className="mt-5 font-heading text-4xl md:text-5xl tracking-tight leading-[1.1]">
                A Space That Works <span className="italic text-primary">Beautifully</span>
              </h2>
              <p className="mt-6 text-base text-muted-foreground leading-relaxed">
                A great bathroom balances beauty with everyday function. We design layouts, storage, and finishes that feel calm, premium, and effortless to use — built with materials that stand up to daily life.
              </p>
              <Link
                to="/contact"
                data-testid="bathrooms-consultation-button"
                className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground group"
              >
                Get a Free Consultation
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <ul className="divide-y divide-border border-y border-border">
                {FEATURES.map((f, i) => (
                  <li key={f} data-testid={`bathrooms-feature-${i + 1}`} className="flex items-start gap-4 py-5">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-sm md:text-base text-foreground/85 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>02 / Bathroom Inspiration</Overline>
            <h2 className="mt-5 mb-14 font-heading text-4xl md:text-5xl tracking-tight">
              Bathrooms & <span className="italic text-primary">Vanities</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {bathProjects.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div data-testid={`bathrooms-gallery-${i + 1}`} className="group relative overflow-hidden aspect-[16/11]">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <h3 className="absolute bottom-5 left-5 font-heading text-2xl text-white tracking-tight">{p.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready for a Better"
        italic="Bathroom?"
        text="Tell us about your bathroom project — from a custom vanity to a full renovation — and we'll design a space you'll enjoy every day."
      />
    </main>
  );
}
